<template>
  <div>
    <div class="alert alert-danger" v-if="errorMsg">{{ errorMsg }}</div>

    <div class="card mb-3">
      <div class="card-body">
        <h6 class="card-title">Enrol user</h6>
        <div class="row">
          <div class="col-4">
            <input class="form-control" v-model="userSearch" placeholder="Search name or e-mail…" @keyup.enter="searchUsers" />
          </div>
          <div class="col-2 d-flex align-items-center">
            <button class="btn btn-outline-primary gdq-font-size-80" :disabled="searching" @click="searchUsers">Search</button>
          </div>
          <div class="col-6">
            <select class="form-select" v-model="selectedUserId" v-if="userResults.length > 0">
              <option value="" disabled>Select user…</option>
              <option v-for="u in userResults" :key="u.id" :value="u.id">{{ u.consultant_name }} ({{ u.email }})</option>
            </select>
            <div class="text-muted" v-else-if="searched">No users found</div>
          </div>
        </div>
        <div class="mt-2">
          <button class="btn btn-primary float-end" :disabled="saving || !selectedUserId" @click="enrol">
            <fa-icon name="check" />Enrol
          </button>
        </div>
      </div>
    </div>

    <table class="table table-hover lh-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>E-mail</th>
          <th>Status</th>
          <th>Enrolled</th>
          <th>Progress</th>
          <th v-for="fs in facilitatorSteps" :key="fs.id">
            <small>{{ fs.title }}</small>
          </th>
          <th style="width: 40px;">⚡</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in rows" :key="item.id">
          <td>{{ item.first_name }} {{ item.family_name }}</td>
          <td>{{ item.email }}</td>
          <td>
            <span class="badge" :class="statusBadge(item.status)">{{ item.status }}</span>
          </td>
          <td>{{ fmtDate(item.enrolled_at) }}</td>
          <td>{{ item.steps_done }}/{{ item.steps_total }}</td>
          <td v-for="fs in facilitatorSteps" :key="fs.id">
            <input type="checkbox" class="form-check-input" :disabled="toggling"
              :checked="isDone(item, fs.id)" @change="toggleStep(item, fs.id, $event)" />
          </td>
          <td>
            <span style="cursor: pointer;" title="Remove enrolment" @click="delRow(item)">
              <fa-icon name="trash-alt" />
            </span>
          </td>
        </tr>
        <tr v-if="rows.length === 0">
          <td :colspan="5 + facilitatorSteps.length + 1" class="text-center text-muted py-3">No enrolments</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  name: 'LHCourseEnrolments',
  props: {
    courseId: { type: Number, required: true },
    steps: { type: Array, required: true }
  },
  data () {
    return {
      rows: [],
      loading: true,
      errorMsg: '',
      userSearch: '',
      userResults: [],
      searched: false,
      searching: false,
      selectedUserId: '',
      saving: false,
      toggling: false
    }
  },
  computed: {
    facilitatorSteps () {
      return this.steps.filter(s => s.facilitator_marked)
    }
  },
  created () {
    this.fetchRows()
  },
  methods: {
    fmtDate (s) {
      return s ? String(s).substring(0, 10) : '-'
    },
    statusBadge (s) {
      if (s === 'enrolled') return 'bg-warning text-dark'
      if (s === 'completed') return 'bg-success'
      if (s === 'cancelled') return 'bg-danger'
      return 'bg-light text-dark'
    },
    isDone (enr, stepId) {
      return (enr.steps_done_ids || []).includes(stepId)
    },
    async fetchRows () {
      this.loading = true
      try {
        const res = await this.$store.dispatch('learninghub_enrolments_fetch', { course_id: this.courseId })
        this.rows = res.data.result || []
      } catch (e) {
        this.errorMsg = e.response && e.response.data && typeof e.response.data.result === 'string'
          ? e.response.data.result : (e.message || 'Fetch failed')
      }
      this.loading = false
    },
    async searchUsers () {
      this.searching = true
      this.errorMsg = ''
      this.selectedUserId = ''
      try {
        const filter = {}
        if (this.userSearch !== '') {
          filter.family_name = { operator: 'LIKE', value: '%' + this.userSearch + '%' }
          filter.first_name = { operator: 'LIKE', value: '%' + this.userSearch + '%' }
          filter.email = { operator: 'LIKE', value: '%' + this.userSearch + '%' }
        }
        const res = await this.$store.dispatch('consultants_fetch', {
          start: 1,
          limit: 25,
          orderby: 'id',
          order: 'ASC',
          filter,
          label_id: 0,
          usergroup_id: 0,
          consultantstatus_id: 0,
          deleted: 0,
          enterprise_id: 0
        })
        const result = Array.isArray(res.data && res.data.result) ? res.data.result : []
        this.userResults = result.map(r => ({ id: r.id, consultant_name: r.consultant_name, email: r.email }))
        this.searched = true
      } catch (e) {
        this.errorMsg = e.response && e.response.data && typeof e.response.data.result === 'string'
          ? e.response.data.result : (e.message || 'Search failed')
      }
      this.searching = false
    },
    async enrol () {
      this.saving = true
      this.errorMsg = ''
      try {
        await this.$store.dispatch('learninghub_enrolment_save', {
          body: { course_id: this.courseId, user_id: this.selectedUserId }
        })
        this.userSearch = ''
        this.userResults = []
        this.searched = false
        this.selectedUserId = ''
        await this.fetchRows()
        this.$emit('changed')
      } catch (e) {
        this.errorMsg = e.response && e.response.data && typeof e.response.data.result === 'string'
          ? e.response.data.result : (e.message || 'Enrol failed')
      }
      this.saving = false
    },
    async delRow (r) {
      if (!confirm('Remove ' + r.first_name + ' ' + r.family_name + ' from this course? Their progress will be deleted.')) return
      try {
        await this.$store.dispatch('learninghub_enrolment_delete', { id: r.id })
        await this.fetchRows()
        this.$emit('changed')
      } catch (e) {
        this.errorMsg = e.response && e.response.data && typeof e.response.data.result === 'string'
          ? e.response.data.result : (e.message || 'Delete failed')
      }
    },
    async toggleStep (enr, stepId, ev) {
      this.toggling = true
      this.errorMsg = ''
      try {
        const res = await this.$store.dispatch('learninghub_facilitator_progress', {
          body: { enrolment_id: enr.id, step_id: stepId, done: ev.target.checked }
        })
        enr.steps_done_ids = res.data.result.steps_done
        enr.steps_done = enr.steps_done_ids.length
      } catch (e) {
        this.errorMsg = e.response && e.response.data && typeof e.response.data.result === 'string'
          ? e.response.data.result : (e.message || 'Update failed')
        this.fetchRows()
      }
      this.toggling = false
    }
  }
}
</script>

<style>
.lh-table {
  font-size: 0.9rem !important;
}
</style>
